import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { 
  Plus, 
  Pencil, 
  Trash2, 
  Layers,
  Ship,
  Car,
  Truck,
  Settings,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { DredgeIcon, WheelLoaderIcon, EngineIcon, TugboatIcon } from '@/components/icons/CustomIcons';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconOptions = [
  { value: 'ship', label: 'Rebocador', Icon: TugboatIcon },
  { value: 'dredge', label: 'Draga de Polpa', Icon: DredgeIcon },
  { value: 'car', label: 'Veículo Leve', Icon: Car },
  { value: 'truck', label: 'Caminhão', Icon: Truck },
  { value: 'loader', label: 'Pá Carregadeira', Icon: WheelLoaderIcon },
  { value: 'engine', label: 'Motor Estacionário', Icon: EngineIcon },
  { value: 'settings', label: 'Outros', Icon: Settings },
];

const EquipmentTypesPage = () => {
  const { t } = useTranslation();
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    icon: 'ship',
    description: '',
    category: '',
    default_tag_prefix: '',
    uses_fuel: false,
  });

  const fetchTypes = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/equipment-types`);
      setTypes(response.data);
    } catch (error) {
      console.error('Error fetching types:', error);
      toast.error(t('errorOccurred'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchTypes();
  }, [fetchTypes]);

  const handleOpenDialog = (type = null) => {
    if (type) {
      setFormData({
        name: type.name,
        icon: type.icon,
        description: type.description || '',
        category: type.category || '',
        default_tag_prefix: type.default_tag_prefix || '',
        uses_fuel: type.uses_fuel || false,
      });
      setSelectedType(type);
    } else {
      setFormData({
        name: '',
        icon: 'ship',
        description: '',
        category: '',
        default_tag_prefix: '',
        uses_fuel: false,
      });
      setSelectedType(null);
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (selectedType) {
        await axios.put(`${API}/equipment-types/${selectedType.id}`, formData);
      } else {
        await axios.post(`${API}/equipment-types`, formData);
      }
      toast.success(t('saveSuccess'));
      setDialogOpen(false);
      fetchTypes();
    } catch (error) {
      console.error('Error saving type:', error);
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/equipment-types/${selectedType.id}`);
      toast.success(t('deleteSuccess'));
      setDeleteDialogOpen(false);
      setSelectedType(null);
      fetchTypes();
    } catch (error) {
      console.error('Error deleting type:', error);
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    }
  };

  const getIconComponent = (iconName) => {
    const found = iconOptions.find(opt => opt.value === iconName);
    return found ? found.Icon : Settings;
  };

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t('equipmentTypes')}</h1>
          <p className="text-muted-foreground">Gerencie os tipos de equipamentos</p>
        </div>
        <Button onClick={() => handleOpenDialog()} className="gap-2" data-testid="add-type-btn">
          <Plus className="w-4 h-4" />
          {t('addEquipmentType')}
        </Button>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <ScrollArea className="w-full">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('typeIcon')}</TableHead>
                  <TableHead>{t('typeName')}</TableHead>
                  <TableHead>Categoria</TableHead>
                  <TableHead>Prefixo TAG</TableHead>
                  <TableHead>{t('description')}</TableHead>
                  <TableHead className="text-right">{t('edit')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                    </TableCell>
                  </TableRow>
                ) : types.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      <Layers className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      {t('noEquipmentTypes')}
                    </TableCell>
                  </TableRow>
                ) : (
                  types.map((type) => {
                    const IconComponent = getIconComponent(type.icon);
                    return (
                      <TableRow key={type.id} data-testid={`type-row-${type.id}`}>
                        <TableCell>
                          <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                            <IconComponent className="w-5 h-5 text-primary" />
                          </div>
                        </TableCell>
                        <TableCell className="font-medium">{type.name}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{type.category || '—'}</TableCell>
                        <TableCell className="font-mono text-sm">{type.default_tag_prefix || '—'}</TableCell>
                        <TableCell className="text-muted-foreground">{type.description || '—'}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleOpenDialog(type)}
                              data-testid={`edit-type-${type.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => {
                                setSelectedType(type);
                                setDeleteDialogOpen(true);
                              }}
                              data-testid={`delete-type-${type.id}`}
                            >
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {selectedType ? t('editEquipmentType') : t('addEquipmentType')}
            </DialogTitle>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">{t('typeName')}</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                data-testid="type-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label>{t('typeIcon')}</Label>
              <Select
                value={formData.icon}
                onValueChange={(value) => setFormData({ ...formData, icon: value })}
              >
                <SelectTrigger data-testid="type-icon-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {iconOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      <div className="flex items-center gap-2">
                        <option.Icon className="w-4 h-4" />
                        {option.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">{t('description')}</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                data-testid="type-description-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="category">Categoria</Label>
                <Input
                  id="category"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  placeholder="Ex: maritime, terrestrial"
                  data-testid="type-category-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="default_tag_prefix">Prefixo TAG</Label>
                <Input
                  id="default_tag_prefix"
                  value={formData.default_tag_prefix}
                  onChange={(e) => setFormData({ ...formData, default_tag_prefix: e.target.value.toUpperCase() })}
                  placeholder="Ex: REB, DRG, VEI"
                  maxLength={5}
                  data-testid="type-prefix-input"
                />
                <p className="text-xs text-muted-foreground">
                  Usado para gerar TAGs automáticas (ex: REB-0001)
                </p>
              </div>
            </div>

            {/* Usa combustível (D-18): gate do Controle de Combustível por TIPO */}
            <div className="flex items-center justify-between rounded-lg border border-border p-3">
              <div className="space-y-0.5 pr-4">
                <Label htmlFor="uses_fuel">Usa combustível</Label>
                <p className="text-xs text-muted-foreground">
                  Equipamentos deste tipo entram no Controle de Combustível (saldo, abastecimento, tanque).
                </p>
              </div>
              <Switch
                id="uses_fuel"
                checked={formData.uses_fuel}
                onCheckedChange={(checked) => setFormData({ ...formData, uses_fuel: checked })}
                data-testid="type-uses-fuel-switch"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              {t('cancel')}
            </Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-type-btn">
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              {t('save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('deleteEquipmentType')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('confirmDelete')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90">
              {t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default EquipmentTypesPage;
